import { useMemo } from 'react'

/**
 * A small, safe markdown renderer.
 *
 * Deliberately not a library: the agent's output is trusted prose but never
 * trusted HTML, so this escapes everything first and then applies a short list
 * of inline and block rules. Anything it does not recognize renders as text —
 * which is the right failure mode.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function inline(s: string): string {
  return escapeHtml(s)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Bold first, and it may hold an italic title ("**Reread *Rat in the
    // Mud* today**"): the shortest run to a closing pair that is not the
    // start of a longer one, so "*title***" closes the italic, then the bold.
    .replace(/\*\*(?=\S)(.+?)\*\*(?!\*)/g, '<strong>$1</strong>')
    .replace(/(^|[\s(>])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    // Links: only http(s), and never with a javascript: payload.
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
}

/** Markdown to safe html; exported for its tests. */
export function render(text: string): string {
  const lines = text.split('\n')
  const out: string[] = []
  let inCode = false
  let codeBuf: string[] = []
  let listType: 'ul' | 'ol' | null = null

  const closeList = () => {
    if (listType) { out.push(`</${listType}>`); listType = null }
  }

  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      if (inCode) {
        out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`)
        codeBuf = []; inCode = false
      } else { closeList(); inCode = true }
      continue
    }
    if (inCode) { codeBuf.push(line); continue }

    if (!line.trim()) { closeList(); continue }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    if (heading) {
      closeList()
      const level = Math.min(6, heading[1]!.length + 1)
      out.push(`<h${level}>${inline(heading[2]!)}</h${level}>`)
      continue
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line)
    if (bullet) {
      if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul' }
      out.push(`<li>${inline(bullet[1]!)}</li>`)
      continue
    }

    const numbered = /^\d+\.\s+(.*)$/.exec(line)
    if (numbered) {
      if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol' }
      out.push(`<li>${inline(numbered[1]!)}</li>`)
      continue
    }

    if (/^>\s?/.test(line)) {
      closeList()
      out.push(`<blockquote>${inline(line.replace(/^>\s?/, ''))}</blockquote>`)
      continue
    }

    closeList()
    out.push(`<p>${inline(line)}</p>`)
  }

  if (inCode && codeBuf.length) {
    out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`)
  }
  closeList()
  return out.join('')
}

export function Markdown({ text }: { text: string }) {
  const html = useMemo(() => render(text), [text])
  return <div className="prose" dangerouslySetInnerHTML={{ __html: html }} />
}
